/**
 * DeckViewer — pitch-deck-style display of a captured meeting.
 *
 * Design principle: NO ADDITIONAL AI INFERENCE. The deck is built
 * by a deterministic transformation of the meeting's already-parsed
 * structured data (summary, speakers, topics, decisions, action
 * items, open questions, risks, entities). Every slide bullet that
 * makes a factual claim carries a verbatim transcript quote. The
 * viewer never invents content — if a meeting has no decisions, the
 * "Decisions" slide doesn't appear.
 *
 * Visual language: Coast's locked design system. Navy background,
 * gold accents, Fraunces titles, EB Garamond body. Slides are full-
 * screen with prev/next navigation, slide counter, ESC to close.
 *
 * PDF export: ships the deck JSON to the backend's /me/deck-pdf
 * endpoint which uses reportlab to render an institutionally-styled
 * landscape PDF (one slide per page).
 */
import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';

const API = 'https://coast-terminal-production.up.railway.app';

// Light theme matching Dashboard — that's the page the user clicks
// "Generate deck" from in 90%+ of cases. White surface, navy text,
// bronze-gold accent — keeps the deck feeling continuous with the
// rest of the app instead of "a dark thing floating over a light app".
const BG          = '#FFFFFF';
const SURFACE     = '#FAFAF8';
const SURFACE_HI  = '#F5F0E8';
const BORDER      = '#E6DDD4';
const ACCENT      = '#b8956a';   // Dashboard's actual gold
const ACCENT_2    = '#9d7d56';
const TEXT        = '#0D3D5C';
const TEXT_DIM    = '#6B7280';
const TEXT_FAINT  = '#9CA3AF';
const RED         = '#CC2200';

/* ── Types mirror the meeting card's parsed payload ──────────────────── */
interface Speaker     { id: string; name?: string | null; role_guess?: string | null;
                         confidence: 'high' | 'medium' | 'low'; share_pct: number; }
interface Topic       { label: string; summary: string; anchor: string; }
interface Decision    { decision: string; quote: string; }
interface ActionItem  { action: string; owner: string | null; deadline: string | null; quote: string; }
interface OpenQ       { question: string; quote: string; }
interface Risk        { risk: string; quote: string; }
interface Entities {
  addresses: string[]; properties: string[]; tribes: string[];
  regulations: string[]; people: string[]; money: string[];
}
export interface ParsedMeeting {
  summary:        string;
  speakers:       Speaker[];
  topics:         Topic[];
  decisions:      Decision[];
  action_items:   ActionItem[];
  open_questions: OpenQ[];
  risks_flagged:  Risk[];
  entities:       Entities;
}
export interface MeetingData {
  recorded_at:  string;
  duration_sec: number;
  transcript:   string;
  parsed:       ParsedMeeting | null;
}

/* Voice-memo source shape for the deck — a memo gets its own slide set
 * (property facts, notes, issues, amenities, entities) since its
 * structured payload is real-estate-specific instead of meeting-flavoured. */
export interface MemoExtracted {
  property_name?:     string | null;
  location?:          string | null;
  address?:           string | null;
  property_type?:     string | null;
  units?:             number | null;
  year_built?:        number | null;
  purchase_price?:    number | null;
  rent_monthly?:      number | null;
  cap_rate?:          number | null;
  noi_monthly?:       number | null;
  cash_flow_monthly?: number | null;
  sqft?:              number | null;
  amenities?:         string[];
  issues?:            string[];
  notes?:             string[];
  summary?:           string;
}
export interface MemoData {
  recorded_at: string;
  transcript:  string;
  extracted:   MemoExtracted | null;
  entities?:   Entities;
}

/* For the combined deck: a discriminated-union list of sources the
 * user picks from the Board. Each one becomes a section of the
 * combined deck, prefixed by a divider slide that names the source. */
export type CombinedSource =
  | { kind: 'memo';    memo:    MemoData;    title: string }
  | { kind: 'meeting'; meeting: MeetingData; title: string };

/* ── Slide shape ─────────────────────────────────────────────────────── */
type SlideKind =
  | 'cover'
  | 'speakers'
  | 'topics'
  | 'decisions'
  | 'actions'
  | 'open-questions'
  | 'risks'
  | 'entities'
  | 'sources'
  | 'section';        // divider slide inside a combined deck

interface Bullet {
  text:     string;
  meta?:    string;
  quote?:   string;
  // For entity bullets — clickable address chips that open the Map.
  href?:    string;
}
interface Slide {
  kind:     SlideKind;
  number:   number;
  title:    string;
  subtitle?: string;
  bullets?: Bullet[];
  // For the cover slide — large display text instead of bullets.
  display?: string;
  // Optional small footnote at the bottom of the slide.
  footnote?: string;
}

/* ── Deck builder: pure transformation of parsed meeting → slides ────── */
export function buildDeck(meeting: MeetingData): Slide[] {
  const p = meeting.parsed;
  if (!p) return [];

  const slides: Slide[] = [];
  let slideNo = 0;
  const next = () => { slideNo += 1; return slideNo; };

  const formatDate = (() => {
    try {
      return new Date(meeting.recorded_at).toLocaleString(undefined, {
        month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch { return meeting.recorded_at; }
  })();
  const formatDuration = (() => {
    const s = meeting.duration_sec || 0;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0 ? `${h}h ${m}m`
         : m > 0 ? `${m}m ${sec}s`
         : `${sec}s`;
  })();

  // ── Cover ────────────────────────────────────────────────────────────
  slides.push({
    kind: 'cover',
    number: next(),
    title: 'Meeting brief',
    subtitle: `${formatDuration} · ${formatDate}`,
    display: p.summary || 'No summary captured.',
    footnote: 'Generated by Coast Investment Terminal. Every fact below is grounded in the recorded transcript.',
  });

  // ── Speakers ─────────────────────────────────────────────────────────
  if (p.speakers && p.speakers.length > 0) {
    slides.push({
      kind: 'speakers',
      number: next(),
      title: 'Participants',
      subtitle: 'Speaker labels are AI-guessed from conversational cues.',
      bullets: p.speakers.map(s => ({
        text:  s.name || s.id,
        meta:  [s.role_guess, `${Math.round(s.share_pct)}% of utterances`,
                 `confidence: ${s.confidence}`].filter(Boolean).join(' · '),
      })),
    });
  }

  // ── Topics ───────────────────────────────────────────────────────────
  if (p.topics && p.topics.length > 0) {
    slides.push({
      kind: 'topics',
      number: next(),
      title: 'Topics discussed',
      bullets: p.topics.map(t => ({
        text:  t.label,
        meta:  t.summary,
        quote: t.anchor,
      })),
    });
  }

  // ── Decisions ────────────────────────────────────────────────────────
  if (p.decisions && p.decisions.length > 0) {
    slides.push({
      kind: 'decisions',
      number: next(),
      title: 'Decisions made',
      bullets: p.decisions.map(d => ({
        text:  d.decision,
        quote: d.quote,
      })),
    });
  }

  // ── Action items ─────────────────────────────────────────────────────
  if (p.action_items && p.action_items.length > 0) {
    slides.push({
      kind: 'actions',
      number: next(),
      title: 'Action items',
      bullets: p.action_items.map(a => ({
        text:  a.action,
        meta:  [a.owner && `Owner: ${a.owner}`, a.deadline && `Due: ${a.deadline}`]
                 .filter(Boolean).join(' · ') || undefined,
        quote: a.quote,
      })),
    });
  }

  // ── Open questions ───────────────────────────────────────────────────
  if (p.open_questions && p.open_questions.length > 0) {
    slides.push({
      kind: 'open-questions',
      number: next(),
      title: 'Open questions',
      subtitle: 'Unresolved threads from the conversation.',
      bullets: p.open_questions.map(q => ({
        text:  q.question,
        quote: q.quote,
      })),
    });
  }

  // ── Risks ────────────────────────────────────────────────────────────
  if (p.risks_flagged && p.risks_flagged.length > 0) {
    slides.push({
      kind: 'risks',
      number: next(),
      title: 'Risks flagged',
      bullets: p.risks_flagged.map(r => ({
        text:  r.risk,
        quote: r.quote,
      })),
    });
  }

  // ── Entities ─────────────────────────────────────────────────────────
  if (p.entities) {
    const e = p.entities;
    const bullets: Bullet[] = [];
    for (const a of e.addresses)   bullets.push({ text: a, meta: 'Address',
                                                   href: `/map?intel_address=${encodeURIComponent(a)}` });
    for (const x of e.properties)  bullets.push({ text: x, meta: 'Property' });
    for (const t of e.tribes)      bullets.push({ text: t, meta: 'Tribe' });
    for (const r of e.regulations) bullets.push({ text: r, meta: 'Regulation' });
    for (const p2 of e.people)     bullets.push({ text: p2, meta: 'Person' });
    for (const m of e.money)       bullets.push({ text: m, meta: 'Amount' });
    if (bullets.length > 0) {
      slides.push({
        kind: 'entities',
        number: next(),
        title: 'Mentioned in the meeting',
        subtitle: 'Every entity below was spoken verbatim during the conversation.',
        bullets,
      });
    }
  }

  // ── Sources (closing) ────────────────────────────────────────────────
  slides.push({
    kind: 'sources',
    number: next(),
    title: 'Source',
    subtitle: 'This deck is grounded in the captured recording.',
    display: `Captured ${formatDate} · ${formatDuration} of audio · ${(meeting.transcript || '').length.toLocaleString()} characters of transcript.`,
    footnote: 'Full transcript is preserved on the Board card. Nothing in this deck was inferred — only what was said.',
  });

  return slides;
}


/* ── Deck builder for a voice memo (CRE quick-capture) ──────────────────
 *  Same deterministic transformation principle as buildDeck (meetings) —
 *  pure mapping of already-parsed structured fields into slides. No new
 *  AI inference. Memo decks have a different shape: instead of speakers
 *  / topics / decisions, they have property facts, notes, issues,
 *  amenities. Both kinds of decks close with a Source slide.
 */
export function buildDeckFromMemo(memo: MemoData): Slide[] {
  const ex = memo.extracted;
  if (!ex && (!memo.entities || Object.values(memo.entities).every(arr => arr.length === 0))) {
    return [];
  }
  const slides: Slide[] = [];
  let n = 0;
  const next = () => { n += 1; return n; };

  const formatDate = (() => {
    try {
      return new Date(memo.recorded_at).toLocaleString(undefined, {
        month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch { return memo.recorded_at; }
  })();

  // ── Cover ────────────────────────────────────────────────────────────
  const coverTitle = ex?.property_name || ex?.address || 'Voice memo';
  const coverSub = ex?.location || (ex?.address && ex?.property_name ? ex.address : null);
  slides.push({
    kind: 'cover',
    number: next(),
    title: coverTitle,
    subtitle: coverSub
      ? `${coverSub} · captured ${formatDate}`
      : `Captured ${formatDate}`,
    display: ex?.summary || (memo.transcript ? memo.transcript.slice(0, 220) + (memo.transcript.length > 220 ? '…' : '') : ''),
    footnote: 'Generated by Coast Investment Terminal. Every fact below is grounded in the captured memo.',
  });

  // ── Key facts ────────────────────────────────────────────────────────
  if (ex) {
    const fmtMoney = (n: number) => `$${n.toLocaleString()}`;
    const facts: Bullet[] = [];
    if (ex.property_type)
      facts.push({ text: ex.property_type, meta: 'Type' });
    if (ex.units != null)
      facts.push({ text: `${ex.units} units`, meta: 'Unit count' });
    if (ex.year_built != null)
      facts.push({ text: String(ex.year_built), meta: 'Year built' });
    if (ex.sqft != null)
      facts.push({ text: `${ex.sqft.toLocaleString()} sqft`, meta: 'Size' });
    if (ex.purchase_price != null)
      facts.push({ text: fmtMoney(ex.purchase_price), meta: 'Asking / price' });
    if (ex.cap_rate != null)
      facts.push({ text: `${ex.cap_rate}%`, meta: 'Cap rate' });
    if (ex.noi_monthly != null)
      facts.push({ text: `${fmtMoney(ex.noi_monthly)}/mo`, meta: 'NOI' });
    if (ex.cash_flow_monthly != null)
      facts.push({ text: `${fmtMoney(ex.cash_flow_monthly)}/mo`, meta: 'Cash flow' });
    if (ex.rent_monthly != null)
      facts.push({ text: `${fmtMoney(ex.rent_monthly)}/mo`, meta: 'Rent' });
    if (facts.length > 0) {
      slides.push({
        kind: 'topics',   // reuse 'topics' visual kind for the layout
        number: next(),
        title: 'Property facts',
        bullets: facts,
      });
    }
  }

  // ── Thesis / notes ───────────────────────────────────────────────────
  if (ex?.notes && ex.notes.length > 0) {
    slides.push({
      kind: 'decisions',  // bullets-with-quote feel matches notes well
      number: next(),
      title: 'Thesis & notes',
      bullets: ex.notes.map(n2 => ({ text: n2 })),
    });
  }

  // ── Issues / red flags ───────────────────────────────────────────────
  if (ex?.issues && ex.issues.length > 0) {
    slides.push({
      kind: 'risks',
      number: next(),
      title: 'Issues / red flags',
      bullets: ex.issues.map(i => ({ text: i })),
    });
  }

  // ── Amenities ────────────────────────────────────────────────────────
  if (ex?.amenities && ex.amenities.length > 0) {
    slides.push({
      kind: 'topics',
      number: next(),
      title: 'Amenities',
      bullets: ex.amenities.map(a => ({ text: a })),
    });
  }

  // ── Mentioned entities — same as meeting deck ─────────────────────────
  if (memo.entities) {
    const e = memo.entities;
    const bullets: Bullet[] = [];
    for (const a of e.addresses)   bullets.push({ text: a, meta: 'Address',
                                                   href: `/map?intel_address=${encodeURIComponent(a)}` });
    for (const x of e.properties)  bullets.push({ text: x, meta: 'Property' });
    for (const t of e.tribes)      bullets.push({ text: t, meta: 'Tribe' });
    for (const r of e.regulations) bullets.push({ text: r, meta: 'Regulation' });
    for (const p of e.people)      bullets.push({ text: p, meta: 'Person' });
    for (const m of e.money)       bullets.push({ text: m, meta: 'Amount' });
    if (bullets.length > 0) {
      slides.push({
        kind: 'entities',
        number: next(),
        title: 'Mentioned in this memo',
        subtitle: 'Every entity below was spoken verbatim in the recording.',
        bullets,
      });
    }
  }

  // ── Sources (closing) ────────────────────────────────────────────────
  slides.push({
    kind: 'sources',
    number: next(),
    title: 'Source',
    subtitle: 'This deck is grounded in the captured voice memo.',
    display: `Captured ${formatDate} · ${(memo.transcript || '').length.toLocaleString()} characters of transcript.`,
    footnote: 'Full transcript is preserved on the Board card. Nothing here was inferred — only what was said.',
  });

  return slides;
}


/* ── Combined deck builder: merge N sources into one cohesive deck ────
 *  Each source becomes its own section: a single divider slide naming
 *  the source, followed by that source's "middle" slides (no cover, no
 *  source closer — those are deduped to the combined deck's own cover
 *  and closer).
 *
 *  After all source sections, a merged Entities slide concatenates every
 *  address / property / tribe / regulation / person / money mention
 *  across all sources (deduplicated case-insensitively). One final
 *  Sources slide lists every input source with its date.
 *
 *  Same architectural constraint as the single-source builders: NO
 *  additional AI inference. Pure deterministic transformation. If you
 *  combine the same N sources twice, the output is bit-for-bit identical.
 */
export function buildCombinedDeck(sources: CombinedSource[]): Slide[] {
  if (!sources || sources.length === 0) return [];
  const slides: Slide[] = [];
  let n = 0;
  const next = () => { n += 1; return n; };

  // ── Cover ───────────────────────────────────────────────────────────
  const sourceList = sources.map(s => s.title).join('  ·  ');
  slides.push({
    kind: 'cover',
    number: next(),
    title: 'Combined brief',
    subtitle: `${sources.length} captured source${sources.length === 1 ? '' : 's'}`,
    display: sourceList,
    footnote: 'Each section below is a verbatim-grounded view of one capture. Nothing across sources was synthesized — only stitched.',
  });

  // ── Per-source sections ─────────────────────────────────────────────
  // Build each source's full slide list, then strip the cover (first)
  // and source closer (last) so we don't get N redundant title pages.
  sources.forEach((src, srcIdx) => {
    // Section divider — visually announces a new source within the deck.
    const srcDate = (() => {
      const iso = src.kind === 'meeting' ? src.meeting.recorded_at : src.memo.recorded_at;
      try {
        return new Date(iso).toLocaleString(undefined, {
          month: 'long', day: 'numeric', year: 'numeric',
        });
      } catch { return iso; }
    })();
    slides.push({
      kind: 'section',
      number: next(),
      title: `Source ${srcIdx + 1} of ${sources.length}`,
      subtitle: src.kind === 'meeting' ? `Meeting · ${srcDate}` : `Voice memo · ${srcDate}`,
      display: src.title,
    });

    // Generate the source's own slides, drop cover + closer, renumber.
    const sub = src.kind === 'meeting'
      ? buildDeck(src.meeting)
      : buildDeckFromMemo(src.memo);
    const middle = sub.filter(s => s.kind !== 'cover' && s.kind !== 'sources');
    for (const s of middle) {
      slides.push({ ...s, number: next() });
    }
  });

  // ── Merged entities (deduped across all sources) ────────────────────
  // Re-walk all sources, harvest each one's entities, dedupe case-
  // insensitively, then render a single combined Mentioned slide.
  const harvest = (e?: Entities) => e || {
    addresses: [], properties: [], tribes: [],
    regulations: [], people: [], money: [],
  };
  const dedupe = (xs: string[]) => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const x of xs) {
      const k = x.trim().toLowerCase();
      if (!k || seen.has(k)) continue;
      seen.add(k); out.push(x.trim());
    }
    return out;
  };

  const merged: Entities = {
    addresses:   [], properties:  [], tribes:     [],
    regulations: [], people:      [], money:      [],
  };
  for (const src of sources) {
    const e = harvest(
      src.kind === 'meeting' ? src.meeting.parsed?.entities : src.memo.entities,
    );
    merged.addresses.push(...e.addresses);
    merged.properties.push(...e.properties);
    merged.tribes.push(...e.tribes);
    merged.regulations.push(...e.regulations);
    merged.people.push(...e.people);
    merged.money.push(...e.money);
  }
  merged.addresses   = dedupe(merged.addresses);
  merged.properties  = dedupe(merged.properties);
  merged.tribes      = dedupe(merged.tribes);
  merged.regulations = dedupe(merged.regulations);
  merged.people      = dedupe(merged.people);
  merged.money       = dedupe(merged.money);

  const mergedTotal = merged.addresses.length + merged.properties.length
                    + merged.tribes.length + merged.regulations.length
                    + merged.people.length + merged.money.length;
  if (mergedTotal > 0) {
    const bullets: Bullet[] = [];
    for (const a of merged.addresses)   bullets.push({ text: a, meta: 'Address',
                                                        href: `/map?intel_address=${encodeURIComponent(a)}` });
    for (const x of merged.properties)  bullets.push({ text: x, meta: 'Property' });
    for (const t of merged.tribes)      bullets.push({ text: t, meta: 'Tribe' });
    for (const r of merged.regulations) bullets.push({ text: r, meta: 'Regulation' });
    for (const p of merged.people)      bullets.push({ text: p, meta: 'Person' });
    for (const m of merged.money)       bullets.push({ text: m, meta: 'Amount' });
    slides.push({
      kind: 'entities',
      number: next(),
      title: 'Mentioned across all sources',
      subtitle: 'Deduplicated. Every chip below appears verbatim in at least one capture.',
      bullets,
    });
  }

  // ── Closing Sources slide ───────────────────────────────────────────
  slides.push({
    kind: 'sources',
    number: next(),
    title: 'Sources',
    subtitle: 'This combined brief is grounded in the captures below.',
    bullets: sources.map((s, i) => {
      const iso = s.kind === 'meeting' ? s.meeting.recorded_at : s.memo.recorded_at;
      const dt = (() => {
        try {
          return new Date(iso).toLocaleString(undefined, {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit',
          });
        } catch { return iso; }
      })();
      return {
        text: s.title,
        meta: s.kind === 'meeting' ? `Meeting · ${dt}` : `Voice memo · ${dt}`,
      };
    }),
    footnote: 'Nothing across sources was inferred — only their contents stitched together.',
  });

  return slides;
}


/* ─────────────────────────────────────────────────────────────────────
 *                            COMPONENT
 * ──────────────────────────────────────────────────────────────────── */
interface ViewerProps {
  // Pass EITHER meeting OR memo OR combined.
  //   meeting  → buildDeck
  //   memo     → buildDeckFromMemo
  //   combined → buildCombinedDeck (N sources stitched into one deck)
  // All three produce the same Slide[] shape that the viewer + PDF
  // renderer consume.
  meeting?: MeetingData;
  memo?:    MemoData;
  combined?: CombinedSource[];
  // Title used for the PDF download filename + the doc metadata.
  // Defaults sensibly when omitted.
  pdfTitle?: string;
  token:   string;
  onClose: () => void;
}

export default function DeckViewer({ meeting, memo, combined, pdfTitle, token, onClose }: ViewerProps) {
  const slides = useMemo(() => {
    if (meeting)  return buildDeck(meeting);
    if (memo)     return buildDeckFromMemo(memo);
    if (combined) return buildCombinedDeck(combined);
    return [];
  }, [meeting, memo, combined]);
  // For PDF naming: prefer the explicit pdfTitle, else derive from source.
  const docTitle = pdfTitle
    || (meeting   ? 'Meeting brief'
       : memo     ? (memo.extracted?.property_name || 'Memo brief')
       : combined ? `Combined brief · ${combined.length} sources`
                  : 'Brief');
  const firstStamp = combined && combined.length > 0
    ? (combined[0].kind === 'meeting' ? combined[0].meeting.recorded_at : combined[0].memo.recorded_at)
    : null;
  const sourceStamp = (meeting?.recorded_at || memo?.recorded_at || firstStamp || new Date().toISOString());
  const sourceDuration = meeting?.duration_sec ?? 0;
  const [idx, setIdx] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Clamp index if the deck changes (it shouldn't, but defensive).
  useEffect(() => {
    if (idx > slides.length - 1) setIdx(Math.max(0, slides.length - 1));
  }, [slides.length, idx]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        setIdx(i => Math.min(slides.length - 1, i + 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        setIdx(i => Math.max(0, i - 1));
      } else if (e.key === 'Home') setIdx(0);
      else if (e.key === 'End') setIdx(slides.length - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slides.length, onClose]);

  const downloadPdf = async () => {
    setDownloading(true);
    setPdfError(null);
    try {
      const r = await axios.post(`${API}/me/deck-pdf`, {
        title:        docTitle,
        recorded_at:  sourceStamp,
        duration_sec: sourceDuration,
        slides,
      }, {
        headers:      { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout:      90_000,
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      const stamp = new Date(sourceStamp).toISOString().slice(0, 16).replace('T', '-').replace(/:/g, '');
      const prefix = meeting ? 'meeting' : memo ? 'memo' : 'combined';
      a.download = `coast-${prefix}-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      const detail = e?.response?.data?.detail
                  || e?.response?.statusText
                  || e?.message;
      setPdfError(`PDF generation failed: ${detail || 'unknown'}`);
    } finally {
      setDownloading(false);
    }
  };

  if (slides.length === 0) {
    return ReactDOM.createPortal(
      <Overlay onClose={onClose}>
        <div style={{
          background: SURFACE, padding: 32, border: `1px solid ${BORDER}`,
          borderRadius: 4, color: TEXT,
        }}>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 18 }}>
            This {meeting ? 'meeting' : memo ? 'memo' : 'selection'} has no parsed content to render as a deck.
          </div>
          <button onClick={onClose} style={btnPrimary}>Close</button>
        </div>
      </Overlay>,
      document.body,
    );
  }

  const slide = slides[idx];

  return ReactDOM.createPortal(
    <Overlay onClose={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width:  'min(1120px, 96vw)',
          height: 'min(720px, 90vh)',
          background: BG, color: TEXT,
          border: `1px solid ${BORDER}`,
          borderRadius: 6,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          fontFamily: '"EB Garamond", "Garamond", serif',
          boxShadow: '0 36px 120px rgba(0,0,0,0.7)',
        }}
      >
        {/* ── Top bar ─────────────────────────────────────────────── */}
        <div style={{
          padding: '12px 22px', borderBottom: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'center', gap: 14,
          background: SURFACE, flexShrink: 0,
        }}>
          <div style={{
            fontFamily: 'Fraunces, serif', fontSize: 14,
            color: ACCENT, letterSpacing: '0.16em',
          }}>
            COAST · DECK
          </div>
          <div style={{
            fontSize: 11, color: TEXT_DIM, letterSpacing: '0.04em',
          }}>
            {slide.number} / {slides.length}
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={downloadPdf} disabled={downloading}
            style={{
              ...btnSecondary,
              opacity: downloading ? 0.5 : 1,
              cursor: downloading ? 'wait' : 'pointer',
            }}>
            {downloading ? 'Building PDF…' : 'Download PDF'}
          </button>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: TEXT_DIM,
            fontSize: 22, cursor: 'pointer', padding: 0, lineHeight: 1,
          }}>×</button>
        </div>

        {/* ── Slide canvas ────────────────────────────────────────── */}
        <div style={{
          flex: 1, position: 'relative', minHeight: 0,
          display: 'flex', alignItems: 'stretch', justifyContent: 'center',
          padding: '36px 64px',
        }}>
          <SlideView slide={slide} />
        </div>

        {/* ── Nav strip ───────────────────────────────────────────── */}
        <div style={{
          padding: '12px 22px', borderTop: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'center', gap: 10,
          background: SURFACE, flexShrink: 0,
        }}>
          <button
            onClick={() => setIdx(i => Math.max(0, i - 1))}
            disabled={idx === 0}
            style={{ ...btnGhost, opacity: idx === 0 ? 0.35 : 1 }}>
            ← Previous
          </button>
          <span style={{ flex: 1, textAlign: 'center', color: TEXT_FAINT,
                          fontSize: 11, letterSpacing: '0.08em',
                          textTransform: 'uppercase' }}>
            {slide.kind.replace('-', ' ')}
          </span>
          <button
            onClick={() => setIdx(i => Math.min(slides.length - 1, i + 1))}
            disabled={idx === slides.length - 1}
            style={{ ...btnGhost, opacity: idx === slides.length - 1 ? 0.35 : 1 }}>
            Next →
          </button>
        </div>

        {pdfError && (
          <div style={{
            padding: '8px 22px', background: '#FBEAE7',
            color: RED, fontSize: 12, borderTop: `1px solid ${RED}55`,
          }}>
            {pdfError}
          </div>
        )}
      </div>
    </Overlay>,
    document.body,
  );
}


/* ── The actual slide ────────────────────────────────────────────── */
function SlideView({ slide }: { slide: Slide }) {
  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column',
      padding: '12px 8px',
      overflow: 'hidden',
    }}>
      {/* Slide header */}
      <div style={{ flexShrink: 0, marginBottom: 24 }}>
        <div style={{
          fontSize: 10, color: ACCENT, letterSpacing: '0.22em',
          textTransform: 'uppercase', marginBottom: 8,
        }}>
          {slide.kind === 'cover' ? 'Brief' : `Slide ${slide.number}`}
        </div>
        <div style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontSize: slide.kind === 'cover' ? 42 : 32,
          color: TEXT, lineHeight: 1.15, letterSpacing: '0.01em',
        }}>
          {slide.title}
        </div>
        {slide.subtitle && (
          <div style={{
            marginTop: 8, color: TEXT_DIM, fontSize: 14,
            fontStyle: 'italic', lineHeight: 1.5,
          }}>
            {slide.subtitle}
          </div>
        )}
      </div>

      {/* Slide body */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {slide.display && (
          <div style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: slide.kind === 'cover' ? 28 : 20,
            color: TEXT, lineHeight: 1.4, fontWeight: 400,
            letterSpacing: '0.01em',
          }}>
            {slide.display}
          </div>
        )}
        {slide.bullets && slide.bullets.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex',
                        flexDirection: 'column', gap: 14 }}>
            {slide.bullets.map((b, i) => <BulletRow key={i} b={b} />)}
          </ul>
        )}
      </div>

      {/* Footnote */}
      {slide.footnote && (
        <div style={{
          flexShrink: 0, marginTop: 18, paddingTop: 14,
          borderTop: `1px solid ${BORDER}`,
          color: TEXT_FAINT, fontSize: 10, letterSpacing: '0.04em',
          fontStyle: 'italic', lineHeight: 1.5,
        }}>
          {slide.footnote}
        </div>
      )}
    </div>
  );
}

function BulletRow({ b }: { b: Bullet }) {
  const handleClick = (e: React.MouseEvent) => {
    if (b.href) {
      e.preventDefault();
      window.history.pushState({}, '', b.href);
      window.dispatchEvent(new Event('popstate'));
    }
  };
  return (
    <li style={{
      paddingLeft: 16, borderLeft: `2px solid ${ACCENT}55`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 10,
        flexWrap: 'wrap',
      }}>
        <div style={{
          fontSize: 17, color: TEXT, lineHeight: 1.4,
          fontWeight: 500,
        }}>
          {b.href ? (
            <a href={b.href} onClick={handleClick} style={{
              color: ACCENT, textDecoration: 'none',
              borderBottom: `1px dotted ${ACCENT}`,
            }}>
              {b.text} →
            </a>
          ) : b.text}
        </div>
        {b.meta && (
          <div style={{
            fontSize: 11, color: ACCENT_2, letterSpacing: '0.06em',
          }}>
            {b.meta}
          </div>
        )}
      </div>
      {b.quote && (
        <div style={{
          marginTop: 6, paddingLeft: 8, borderLeft: `2px solid ${BORDER}`,
          color: TEXT_DIM, fontSize: 12, fontStyle: 'italic',
          lineHeight: 1.55,
        }}>
          "{b.quote}"
        </div>
      )}
    </li>
  );
}


/* ── Overlay helper ───────────────────────────────────────────────── */
function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9500,
        background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
      {children}
    </div>
  );
}


/* ── Buttons ──────────────────────────────────────────────────────── */
const btnPrimary: React.CSSProperties = {
  background: ACCENT, color: '#0a1322', border: 'none',
  padding: '8px 16px', fontSize: 12, fontWeight: 600,
  letterSpacing: '0.06em', textTransform: 'uppercase',
  cursor: 'pointer', borderRadius: 3, fontFamily: 'inherit',
};
const btnSecondary: React.CSSProperties = {
  background: 'transparent', color: TEXT,
  border: `1px solid ${ACCENT}`,
  padding: '7px 14px', fontSize: 11, fontWeight: 600,
  letterSpacing: '0.06em', textTransform: 'uppercase',
  cursor: 'pointer', borderRadius: 3, fontFamily: 'inherit',
};
const btnGhost: React.CSSProperties = {
  background: 'transparent', color: TEXT_DIM,
  border: `1px solid ${BORDER}`,
  padding: '7px 14px', fontSize: 11,
  letterSpacing: '0.04em', textTransform: 'uppercase',
  cursor: 'pointer', borderRadius: 3, fontFamily: 'inherit',
};
